/* ---------------------------------------------------------------------------
   Brand colour → a small, contrast-safe token set for the demo surfaces.
   One primary colour in; out come the tokens every owner-facing mock needs:
     --demo-brand        the primary, as read (fills: buttons, chips, pins, progress)
     --demo-brand-fg     text/icon colour ON the primary (white or ink, whichever reads)
     --demo-brand-text   the primary used AS text/icon on white (darkened until ≥ 4.5:1)
     --demo-brand-deep   a deep shade of the same hue (dark cards, gradients)
     --demo-brand-light  a pale tint of the same hue (labels on deep cards)
     --demo-brand-soft   12% wash (backgrounds, glows on white)
     --demo-brand-glow   45% wash (the halo behind the phone)
   Pure functions, no DOM. Registered as @property in index.css so they transition.
--------------------------------------------------------------------------- */

const INK = "#15130F";
const WHITE = "#FFFFFF";

export function normalizeHex(input, fallback = "#D298FF") {
  if (!input || typeof input !== "string") return fallback;
  let h = input.trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(h)) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return fallback;
  return `#${h.toUpperCase()}`;
}

export function hexToRgb(hex) {
  const h = normalizeHex(hex).slice(1);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

const to2 = (n) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, "0").toUpperCase();
export const rgbToHex = (r, g, b) => `#${to2(r)}${to2(g)}${to2(b)}`;

export function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h / 6, s, l];
}

export function hslToRgb(h, s, l) {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const f = (t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [f(h + 1 / 3) * 255, f(h) * 255, f(h - 1 / 3) * 255];
}

export const hslToHex = (h, s, l) => rgbToHex(...hslToRgb(h, Math.max(0, Math.min(1, s)), Math.max(0, Math.min(1, l))));

export function relativeLuminance(hex) {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Text colour that reads on `hex`: ink or white, whichever has more contrast. */
export function onColor(hex) {
  return contrastRatio(hex, INK) >= contrastRatio(hex, WHITE) ? INK : WHITE;
}

export function rgba(hex, alpha) {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Same hue, lightness pulled down until it reads as text on white. */
export function readableOnWhite(hex, min = 4.5) {
  let c = normalizeHex(hex);
  const [h, s0] = rgbToHsl(...hexToRgb(c));
  let [, , l] = rgbToHsl(...hexToRgb(c));
  let s = Math.max(s0, 0.4);
  let guard = 0;
  while (contrastRatio(c, WHITE) < min && guard < 24) {
    l -= 0.03;
    c = hslToHex(h, s, l);
    guard += 1;
  }
  return c;
}

/** The full token set for one primary colour. */
export function brandTokens(input) {
  const brand = normalizeHex(input);
  const [h, s, l] = rgbToHsl(...hexToRgb(brand));
  const grey = s < 0.08;
  // Dark brands glow in a lighter shade of the same hue — a near-black halo would read as dirt, not light.
  const glowBase = l < 0.42 && !grey ? hslToHex(h, Math.max(s, 0.45), 0.62) : brand;
  return {
    brand,
    fg: onColor(brand),
    text: readableOnWhite(brand),
    deep: grey ? "#1F1D19" : hslToHex(h, Math.min(Math.max(s, 0.32), 0.62), 0.15),
    light: grey ? "#E9E6DF" : hslToHex(h, Math.min(s + 0.08, 0.92), 0.8),
    soft: rgba(glowBase, 0.12),
    glow: rgba(glowBase, 0.42),
  };
}

/** Inline style object that sets every token as a CSS variable on a scope element. */
export function brandStyle(input) {
  const tk = brandTokens(input);
  return {
    "--demo-brand": tk.brand,
    "--demo-brand-fg": tk.fg,
    "--demo-brand-text": tk.text,
    "--demo-brand-deep": tk.deep,
    "--demo-brand-light": tk.light,
    "--demo-brand-soft": tk.soft,
    "--demo-brand-glow": tk.glow,
  };
}

/** Same hue, a touch darker — the hover state of a filled button. */
export function shade(hex, amount = 0.06) {
  const [h, s, l] = rgbToHsl(...hexToRgb(normalizeHex(hex)));
  return hslToHex(h, s, Math.max(0, l - amount));
}

/**
 * The whole marketing page in a visitor's brand: every place the page uses Listed's lavender
 * (headline accent, primary buttons, eyebrow dots, glows, hover colours) reads these tokens.
 * Registered as @property in index.css, so the page *fades* into the new brand.
 */
export function pageBrandStyle(input) {
  const tk = brandTokens(input);
  const [r, g, b] = hexToRgb(tk.text);
  return {
    ...brandStyle(input),
    "--lavender": tk.text,
    "--lavender-rgb": `${r} ${g} ${b}`,
    "--lavender-soft": tk.light,
    "--lavender-cta": tk.brand,
    "--lavender-cta-hover": shade(tk.brand, 0.06),
    "--lavender-cta-fg": tk.fg,
    "--brand-accent": tk.brand,
    "--accent-glow": tk.glow,
    "--accent-soft": tk.soft,
  };
}
